import { Facebook, Globe, Instagram, Linkedin, Twitter, Youtube } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link, Outlet } from 'react-router';

import { Container } from '@/components/ui/Container';
import { MarketingHeader } from '@/features/marketing/components/MarketingHeader';
import { OfferStrip } from '@/features/marketing/components/OfferStrip';
import { useMarketingMeta } from '@/features/marketing/hooks/useMarketingMeta';
import { useTrackPageVisit } from '@/features/marketing/hooks/useTrackPageVisit';
import { PolicyFooterLinks } from '@/features/legal/components/PolicyFooterLinks';
import { BRANDING } from '@/features/marketing/content/branding';
import { FOOTER_COLUMNS, FOOTER_TEXT, SOCIAL_LINKS } from '@/features/marketing/content/footer';
import { SiteContentPreviewBar } from '@/features/siteContent/SiteContentPreviewBar';
import { useSiteContentSync } from '@/features/siteContent/useSiteContentSync';

/** The icon for a social address, from its host. */
function socialIcon(href: string): LucideIcon {
  const host = (() => {
    try {
      return new URL(href).hostname.toLowerCase();
    } catch {
      return '';
    }
  })();
  if (host.includes('instagram')) return Instagram;
  if (host.includes('facebook') || host.includes('fb.')) return Facebook;
  if (host.includes('youtube') || host.includes('youtu.be')) return Youtube;
  if (host.includes('linkedin')) return Linkedin;
  if (host.includes('twitter') || host === 'x.com' || host.endsWith('.x.com')) return Twitter;
  return Globe;
}

function Footer() {
  const socials = SOCIAL_LINKS.filter((social) => social.href.trim() && social.label.trim());
  return (
    <footer className="border-t border-line bg-surface">
      {/* Two link columns side by side from the smallest phone up: stacked one
          per row, the four lists ran to two full screens of scrolling. The
          about block spans both, and from `lg` everything sits in one row.
          The header already carries the logo, so the about block is headed
          like the link columns rather than repeating it. */}
      <Container className="grid grid-cols-2 gap-x-6 gap-y-8 py-10 sm:gap-x-10 lg:grid-cols-[1.5fr_1fr_1fr_1fr_1fr] lg:py-12">
        <div className="col-span-2 lg:col-span-1">
          <p className="text-sm font-semibold text-ink">{BRANDING.name}</p>
          <p className="mt-3 max-w-xs text-sm leading-relaxed text-muted sm:mt-4">
            {FOOTER_TEXT.blurb}
          </p>
          {socials.length > 0 ? (
            <ul className="mt-5 flex items-center gap-2">
              {socials.map((social) => {
                const Icon = socialIcon(social.href);
                return (
                  <li key={`${social.label}-${social.href}`}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noreferrer noopener"
                      aria-label={social.label}
                      className="grid size-9 place-items-center rounded-full border border-line text-muted transition-colors hover:border-brand-300 hover:text-brand-600"
                    >
                      <Icon className="size-4" aria-hidden />
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : null}
        </div>
        {FOOTER_COLUMNS.map((column) => (
          <nav key={`${column.heading}-${column.links.length}`} aria-label={column.heading}>
            <p className="text-sm font-semibold text-ink">{column.heading}</p>
            <ul className="mt-3 space-y-2 sm:mt-4 sm:space-y-2.5">
              {column.links.map((link) => (
                <li key={`${link.label}-${link.to}`}>
                  <Link
                    to={link.to}
                    className="text-sm text-muted transition-colors hover:text-brand-600"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        ))}
      </Container>
      <div className="border-t border-line">
        {/* The policies get a row of their own: squeezed between the
            copyright and the strapline, all three wrapped. Below them the
            copyright and strapline share a row from `sm`, stacked and
            centred on a phone. */}
        <Container className="flex flex-col items-center gap-4 py-5 text-center">
          <PolicyFooterLinks />
          <div className="flex w-full flex-col items-center gap-1.5 border-t border-line pt-4 sm:flex-row sm:justify-between">
            <p className="text-xs text-muted">
              {FOOTER_TEXT.copyright.replace('{year}', String(new Date().getFullYear()))}
            </p>
            <p className="text-xs text-muted">{FOOTER_TEXT.strapline}</p>
          </div>
        </Container>
      </div>
    </footer>
  );
}

export function RootLayout() {
  // Keyed on the content version: the header, pages and footer read the
  // content modules directly, so a newly applied edit needs a fresh render.
  const contentVersion = useSiteContentSync();
  useMarketingMeta();
  useTrackPageVisit();

  return (
    <div className="flex min-h-dvh flex-col">
      <SiteContentPreviewBar />
      {/* Above the sticky header, so it scrolls away and the header stays. */}
      <OfferStrip />
      <MarketingHeader key={`header-${contentVersion}`} />
      <main key={`main-${contentVersion}`} className="flex-1">
        <Outlet />
      </main>
      <Footer key={`footer-${contentVersion}`} />
    </div>
  );
}
